// Google Identity Services client ID (Web client).
import i18n from '@/i18n';

const GOOGLE_CLIENT_ID = '198426606153-alq1briddt33trn9v22jr3qv4qifnqua.apps.googleusercontent.com';

// Apple Sign in with Apple Services ID (web client identifier).
const APPLE_CLIENT_ID = 'io.swiftinvoice.web';

// Redirect URL for Apple web Sign-In; must be registered in the Apple Developer console.
const APPLE_REDIRECT_URI = (window.location.origin || '') + '/apple-sign-in-callback.html';

// Social login providers supported by backend.
const PROVIDER_GOOGLE = 'GOOGLE';
const PROVIDER_APPLE = 'APPLE';

// Generate a random nonce string (url-safe, base64 of random bytes).
function generateRawNonce() {
    const bytes = new Uint8Array(32);
    if (window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(bytes);
    } else {
        // Fallback for very old browsers
        for (let i = 0; i < bytes.length; i++) {
            bytes[i] = Math.floor(Math.random() * 256);
        }
    }
    const hex = Array.prototype.map.call(bytes, function (b) {
        return ('0' + b.toString(16)).slice(-2);
    }).join('');
    return hex;
}

// Compute the SHA-256 hex digest of a string using the Web Crypto API.
function sha256Hex(message) {
    return new Promise(function (resolve, reject) {
        if (window.crypto && window.crypto.subtle && window.crypto.subtle.digest) {
            const encoder = new TextEncoder();
            window.crypto.subtle.digest('SHA-256', encoder.encode(message)).then(function (digestBuffer) {
                const bytes = new Uint8Array(digestBuffer);
                const hex = Array.prototype.map.call(bytes, function (b) {
                    return ('0' + b.toString(16)).slice(-2);
                }).join('');
                resolve(hex);
            }).catch(reject);
        } else {
            reject(new Error('Web Crypto SHA-256 is not available'));
        }
    });
}

// Wait for window.google to be available (script loaded).
function waitForGoogle(maxWaitMs) {
    return new Promise(function (resolve, reject) {
        if (window.google && window.google.accounts && window.google.accounts.id) {
            resolve(window.google.accounts.id);
            return;
        }
        const start = Date.now();
        const timer = setInterval(function () {
            if (window.google && window.google.accounts && window.google.accounts.id) {
                clearInterval(timer);
                resolve(window.google.accounts.id);
            } else if (Date.now() - start > (maxWaitMs || 10000)) {
                clearInterval(timer);
                reject(new Error('Google Identity Services script not loaded'));
            }
        }, 50);
    });
}

// Hidden container that hosts the Google Identity Services button.
// GIS renders its own <div> with an inner button that opens the account picker popup.
let googleButtonContainer = null;
let pendingGoogleResolve = null;
let pendingGoogleReject = null;
let popupOpen = false;
let focusListener = null;

function ensureGoogleButtonContainer() {
    if (googleButtonContainer && document.body.contains(googleButtonContainer)) {
        return googleButtonContainer;
    }
    googleButtonContainer = document.createElement('div');
    googleButtonContainer.style.position = 'fixed';
    googleButtonContainer.style.top = '-9999px';
    googleButtonContainer.style.left = '-9999px';
    googleButtonContainer.style.opacity = '0';
    googleButtonContainer.style.pointerEvents = 'none';
    googleButtonContainer.setAttribute('aria-hidden', 'true');
    document.body.appendChild(googleButtonContainer);
    return googleButtonContainer;
}

// Reset the pending state and tear down the focus listener.
function resetGooglePending() {
    pendingGoogleResolve = null;
    pendingGoogleReject = null;
    popupOpen = false;
    if (focusListener) {
        window.removeEventListener('focus', focusListener);
        focusListener = null;
    }
}

// Lazily initialize GIS and render the standard Google Sign-In button into a
// hidden container. The hidden button is what we later click to open the popup.
// Each call uses a fresh nonce, so we re-render the button on every sign-in.
function prepareGoogleButton(rawNonce) {
    return waitForGoogle().then(function (googleId) {
        googleId.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: function (response) {
                if (response && response.credential) {
                    if (pendingGoogleResolve) {
                        pendingGoogleResolve(response.credential);
                    }
                    resetGooglePending();
                } else if (pendingGoogleReject) {
                    pendingGoogleReject(new Error('Google did not return a credential'));
                    resetGooglePending();
                }
            },
            auto_select: false,
            cancel_on_tap_outside: true,
            nonce: rawNonce
        });
        const container = ensureGoogleButtonContainer();
        container.innerHTML = '';
        googleId.renderButton(
            container,
            {type: 'standard', shape: 'rectangular', size: 'large', width: 240, locale: i18n.locale}
        );
        // GIS renders a clickable element with role="button" inside the container.
        return container.querySelector('[role="button"], button');
    });
}

// Traditional Google Sign-In: render the GIS button off-screen and click it
// to open the account-picker popup. Avoids One Tap so closing the popup never
// permanently blocks a subsequent sign-in attempt.
//
// GIS has no "popup closed" callback. To detect cancellation we listen for the
// window regaining focus after the popup opened: if focus returns and we still
// haven't received a credential, treat it as the user cancelling.
function signInWithGoogle() {
    return new Promise(function (resolve, reject) {
        const rawNonce = generateRawNonce();
        // If a previous attempt is somehow still pending, reject it first.
        if (pendingGoogleReject) {
            pendingGoogleReject(new Error('Google Sign-In superseded'));
            resetGooglePending();
        }
        pendingGoogleResolve = function (idToken) {
            resetGooglePending();
            resolve({idToken: idToken, rawNonce: rawNonce});
        };
        pendingGoogleReject = function (err) {
            resetGooglePending();
            reject(err);
        };
        prepareGoogleButton(rawNonce).then(function (clickable) {
            if (!clickable) {
                pendingGoogleReject(new Error('Google Sign-In button failed to render'));
                return;
            }
            // Mark popup as opening and listen for window focus to detect close.
            popupOpen = true;
            focusListener = function () {
                if (!popupOpen) {
                    return;
                }
                // Defer so any credential callback queued just before focus has a chance to run.
                setTimeout(function () {
                    if (pendingGoogleReject) {
                        pendingGoogleReject(new Error('Google Sign-In was cancelled'));
                    }
                }, 300);
            };
            window.addEventListener('focus', focusListener);
            // Trigger the account-picker popup. If the GIS script was already
            // loaded (typical, since we pre-render on mount), this runs inside
            // the user gesture that called signInWithGoogle.
            clickable.click();
        }).catch(function (err) {
            pendingGoogleReject(err);
        });
    });
}

// Pre-render the hidden GIS button so the first user click opens the popup
// synchronously. Safe to call on mount; no-op if the GIS script is unavailable.
function initGoogleSignIn() {
    const rawNonce = generateRawNonce();
    prepareGoogleButton(rawNonce).catch(function () {
        // GIS script not loaded yet; signInWithGoogle will retry on click.
    });
}

// Wait for window.AppleID to be available (script loaded).
function waitForApple(maxWaitMs) {
    return new Promise(function (resolve, reject) {
        if (window.AppleID && window.AppleID.auth) {
            resolve(window.AppleID.auth);
            return;
        }
        const start = Date.now();
        const timer = setInterval(function () {
            if (window.AppleID && window.AppleID.auth) {
                clearInterval(timer);
                resolve(window.AppleID.auth);
            } else if (Date.now() - start > (maxWaitMs || 10000)) {
                clearInterval(timer);
                reject(new Error('Apple Sign-In JS SDK not loaded'));
            }
        }, 50);
    });
}

// Initialize and run Apple Sign-In on web.
// The backend verifies that the ID token's `nonce` claim equals sha256Hex(rawNonce),
// so we send sha256Hex(rawNonce) as the `nonce` parameter to Apple.
// Returns a Promise that resolves with { idToken, rawNonce } on success.
function signInWithApple() {
    const rawNonce = generateRawNonce();
    return sha256Hex(rawNonce).then(function (hashedNonce) {
        return waitForApple().then(function (appleAuth) {
            appleAuth.init({
                clientId: APPLE_CLIENT_ID,
                scope: 'name email',
                redirectURI: APPLE_REDIRECT_URI,
                state: generateRawNonce(),
                usePopup: true,
                nonce: hashedNonce
            });
            return appleAuth.signIn();
        }).then(function (response) {
            if (response && response.authorization && response.authorization.id_token) {
                return {
                    idToken: response.authorization.id_token,
                    rawNonce: rawNonce
                };
            }
            throw new Error('Apple did not return an id_token');
        });
    });
}

// Send the social login request to the backend.
// axiosInstance is the configured $axios instance from the app.
// Returns a Promise that resolves with the response (axios response).
function socialLogin(axiosInstance, params) {
    return axiosInstance.post('social_login', {
        params: params
    });
}

// Send the bind (password-confirm) request after a BINDING_REQUIRED response.
function bindSocialLogin(axiosInstance, params) {
    return axiosInstance.post('bind_social_login', {
        params: params
    });
}

// Already-logged-in user binds a Google/Apple identity from the settings page.
function bindLoggedInSocialLogin(axiosInstance, params) {
    return axiosInstance.post('bind_logged_in_social_login', {
        params: params
    });
}

export {
    PROVIDER_GOOGLE,
    PROVIDER_APPLE,
    generateRawNonce,
    signInWithGoogle,
    initGoogleSignIn,
    signInWithApple,
    socialLogin,
    bindSocialLogin,
    bindLoggedInSocialLogin
};
